import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Search } from 'lucide-react';
import { fetchApi } from '../config/api';
import { Alert, PageBackdrop, PageHeader } from '../components/UI';
import { notifyDogsChanged } from '../utils/dogEvents';
import { formatDate } from '../utils/formatDate';
import { breedNameFor } from '../utils/dogTransfer';

interface TransferPreview {
  dogName: string;
  breedNameGerman: string | null;
  breedNameEnglish: string | null;
  sex: string;
  dateOfBirth: string | null;
  microchipNumber: string | null;
  fromName: string;
}

/** Kaeufer-Seite der Hunde-Uebergabe: Code eingeben, den Hund ansehen und uebernehmen. */
const TakeOverDog: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<TransferPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const checkCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      setPreview(await fetchApi(`/dog-transfers/preview?code=${encodeURIComponent(code.trim())}`));
    } catch (err: any) {
      setError(err.message || t('transfer:takeOver.error'));
    } finally {
      setLoading(false);
    }
  };

  const accept = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchApi('/dog-transfers/accept', {
        method: 'POST',
        body: JSON.stringify({ code: code.trim() })
      });
      setSuccessMessage(t('transfer:takeOver.success', { name: result.dogName }));
      notifyDogsChanged();
      setTimeout(() => navigate(`/dogs/${result.dogId}`), 1200);
    } catch (err: any) {
      setError(err.message || t('transfer:takeOver.error'));
      setLoading(false);
    }
  };

  const breed = preview ? breedNameFor(preview, i18n.language) : null;
  const details = preview
    ? [
        breed,
        t(`transfer:sex.${preview.sex}`, { defaultValue: preview.sex }),
        preview.dateOfBirth ? formatDate(preview.dateOfBirth) : null,
        preview.microchipNumber ? t('transfer:list.microchip', { number: preview.microchipNumber }) : null
      ].filter(Boolean)
    : [];

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <PageBackdrop />

      <div className="relative z-10 container mx-auto px-4 py-8 sm:px-6 sm:py-10">
        <PageHeader
          title={t('transfer:takeOver.title')}
          subtitle={t('transfer:takeOver.subtitle')}
          backLink={{ label: t('nav:primary.cockpit'), onClick: () => navigate('/dashboard') }}
        />

        <main className="mt-6">
          <div className="card max-w-md space-y-4">
            <form onSubmit={checkCode} className="space-y-4">
              <div>
                <label className="label-base" htmlFor="transferCode">{t('transfer:takeOver.codeLabel')}</label>
                <input
                  className="input-base font-mono uppercase tracking-widest"
                  type="text"
                  id="transferCode"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setPreview(null);
                  }}
                  required
                  placeholder={t('transfer:takeOver.codePlaceholder')}
                  maxLength={16}
                  disabled={!!successMessage}
                />
              </div>
              {!preview && (
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading || !code.trim()}>
                    <Search size={18} />
                    {loading ? t('transfer:takeOver.checking') : t('transfer:takeOver.check')}
                  </button>
                </div>
              )}
            </form>

            {preview && (
              <div className="space-y-4">
                <div className="rounded-lg border border-dark-600/60 p-4">
                  <div className="text-lg font-semibold text-dark-50">{preview.dogName}</div>
                  <div className="text-sm text-dark-300">{details.join(' · ')}</div>
                  <div className="mt-2 text-sm text-dark-400">
                    {preview.fromName
                      ? t('transfer:takeOver.from', { name: preview.fromName })
                      : t('transfer:takeOver.fromUnknown')}
                  </div>
                </div>
                <Alert type="info" message={t('transfer:takeOver.notice')} />
                {!successMessage && (
                  <div className="form-actions">
                    <button type="button" className="btn btn-primary" onClick={accept} disabled={loading}>
                      <Check size={18} />
                      {loading ? t('transfer:takeOver.accepting') : t('transfer:takeOver.accept')}
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => navigate('/dashboard')}>
                      {t('common:actions.cancel')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {error && <Alert type="error" message={error} />}
            {successMessage && <Alert type="success" message={successMessage} />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TakeOverDog;
